import { Entity, FieldType, FileStorageType, PersistenceType } from 'gossamer-unified';
import { PERSONAL_INSTITUTION_ID } from 'cb-unified';

export interface InstitutionRow {
  id: number;
  name: string;
  status: string;
  type: string;
  created_at: string;
  who_can_create_events: string;

  // Formerly InstitutionDetails
  logo: string | null;
  banner: string | null;
  description: string | null;
  visibility_limit: boolean;
  address: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  stripe_connect_account_id: string | null;
  stripe_connect_details_submitted: boolean;
  stripe_connect_charges_enabled: boolean;
  stripe_connect_payouts_enabled: boolean;
  stripe_connect_onboarded_at: string | null;
}

export const Institution: Entity<InstitutionRow> = {
  id: 'Institution',
  title: 'Institution',
  service: 'main',
  rowLabel: (row: InstitutionRow) => row.name,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.institution',
  },
  security: [
    {
      role: 'admin',
      action: 'read',
      access: 'conditional',
      condition: `status = 'ACTIVE' AND id IN (${PERSONAL_INSTITUTION_ID}, {adminInstitutionId})`,
    },
    {
      role: 'admin',
      action: 'update',
      access: 'conditional',
      condition: "status = 'ACTIVE' AND id = {adminInstitutionId}",
    },
    { role: 'staff', action: 'all', access: 'conditional', condition: "status = 'ACTIVE'" },
  ],
  defaultSort: [['name', 'ASC']],
  defaultFilters: [{ fieldId: 'name', operator: 'ct', value: '' }],
  refOrderClause: 'SELECT ZZ.name FROM institution ZZ WHERE ZZ.id',
  listRowLink: '/admin/institution/display/{key}',
  fields: {
    id: {
      seq: 5,
      type: FieldType.Integer,
      label: 'Id',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      listColumn: true,
      autoIncrement: true,
    },
    name: {
      seq: 10,
      type: FieldType.Text,
      label: 'Name',
      mandatory: true,
      listColumn: true,
      maxLength: 80,
      fieldGroup: 'admin-updatable',
    },
    status: {
      seq: 12,
      label: 'Status',
      type: FieldType.Options,
      mandatory: true,
      persistence: PersistenceType.BackEndOnly,
      options: [
        { id: 'ACTIVE', label: 'active' },
        { id: 'INACTIVE', label: 'inactive' },
      ],
    },
    type: {
      seq: 15,
      label: 'Type',
      type: FieldType.Options,
      mandatory: true,
      listColumn: true,
      options: [
        { id: 'paid', label: 'Paid', active: false },
        { id: 'society', label: 'Society', active: false },
        { id: 'marketing', label: 'Marketing', active: false },
        { id: 'enterprise', label: 'Enterprise' },
        { id: 'personal', label: 'Personal' },
        { id: 'pro', label: 'Pro' },
        { id: 'premium', label: 'Premium' },
      ],
    },
    created_at: {
      seq: 20,
      label: 'Created At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    who_can_create_events: {
      seq: 25,
      label: 'Who Can Create Events?',
      type: FieldType.Options,
      mandatory: true,
      description: 'Controls who can create events for this institution',
      options: [
        { id: 'admins_only', label: 'Admins only' },
        { id: 'admins_or_verified', label: 'Admins or members' },
      ],
      fieldGroup: 'admin-updatable',
    },

    logo: {
      seq: 30,
      type: FieldType.File,
      label: 'Logo',
      imagesOnly: true,
      storageType: FileStorageType.ExternalResource,
      fieldGroup: 'admin-updatable',
    },
    banner: {
      seq: 35,
      type: FieldType.File,
      label: 'Banner',
      imagesOnly: true,
      storageType: FileStorageType.ExternalResource,
      fieldGroup: 'admin-updatable',
    },
    description: {
      seq: 40,
      label: 'Description',
      type: FieldType.Richtext,
      fieldGroup: 'admin-updatable',
    },
    visibility_limit: {
      seq: 45,
      label: 'Visibility Limit',
      type: FieldType.Boolean,
      visible: false, // TODO introduce this
      fieldGroup: 'admin-updatable',
    },
    address: {
      seq: 50,
      label: 'Address',
      type: FieldType.Richtext,
      fieldGroup: 'admin-updatable',
    },
    contact_email: {
      seq: 55,
      label: 'Contact Email',
      type: FieldType.Text,
      maxLength: 255,
      regex: '\\w+@\\w+',
      fieldGroup: 'admin-updatable',
    },
    contact_phone: {
      seq: 60,
      label: 'Contact Phone',
      type: FieldType.Text,
      maxLength: 255,
      fieldGroup: 'admin-updatable',
    },
    stripe_connect_account_id: {
      seq: 65,
      label: 'Stripe Connect Account Id',
      type: FieldType.Text,
      persistence: PersistenceType.BackEndManaged,
    },
    stripe_connect_details_submitted: {
      seq: 70,
      label: 'Stripe Connect Details Submitted',
      type: FieldType.Boolean,
      persistence: PersistenceType.BackEndManaged,
    },
    stripe_connect_charges_enabled: {
      seq: 75,
      label: 'Stripe Connect Charges Enabled',
      type: FieldType.Boolean,
      persistence: PersistenceType.BackEndManaged,
    },
    stripe_connect_payouts_enabled: {
      seq: 80,
      label: 'Stripe Connect Payouts Enabled',
      type: FieldType.Boolean,
      persistence: PersistenceType.BackEndManaged,
    },
    stripe_connect_onboarded_at: {
      seq: 85,
      label: 'Stripe Connect Onboarded At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
    },
  },
};
